import React, { useState } from "react"
import { View, StyleSheet, ScrollView, TouchableOpacity } from "react-native"
import { Button } from "@Components/CustomButton/Button"
import { ContainerGlogal } from "@Components/ContainerGlogal"
import { InputGroup } from "@Components/Input/InputGroups"
import { BreeSerifText } from "@Components/CustomText/BreeSerifText"
import { OpenSansText } from "@Components/CustomText/OpenSansText"
import { baseColors } from "@Constants/baseStyles"
import {
    areEqual,
    dbDateFormat,
    isStrongPassword,
    isValidEmail,
    isValideName,
    isValidPassword,
    TransformToLowerCase
} from "@Utils/utils"
import { Spacer } from "@Components/Space"
import { useSetRecoilState } from "recoil"
import { registerEmailAtom } from "@Recoil/Atom/userAtom"
import { PasswordValidatorContent } from "./PasswordValidatorContent"
import { ShowToast } from "@Components/ToastNotification/ToastNotification"
import moment from "moment"
import axios from "axios"
import { AXIOS_HEADERS, REGISTRATION_BASE_URL } from "@Services/AxiosInstance"

const defaultUserInfos = {
    email: "",
    password: {
        first: "",
        second: ""
    },
    firstName: "",
    lastName: "",
    sex: 1,
    birthDate: moment(new Date()).format(dbDateFormat),
    roles: "ROLE_PATIENT",
    category: "PATIENT"
}

export const RegisterPage = ({ navigation }) => {
    const [loading, setLoading] = useState(false)
    const [hasError, setHasError] = useState(false)
    const [errorMessage, setErrorMessage] = useState("")
    const setRegisterEmail = useSetRecoilState(registerEmailAtom)
    const [userInfo, setUserInfo] = useState(defaultUserInfos)
    const [formValidation, setFormValidation] = useState({
        email: true,
        firstName: true,
        lastName: true,
        password: {
            first: true,
            second: true
        }
    })

    const gotoLoginPage = () => navigation.navigate("LoginStack")
    const gotoActivation = () => navigation.navigate("Activation")

    const performRegister = () => {
        const payload = { ...userInfo, rpps: "" }
        setRegisterEmail(userInfo.email)
        setLoading(true)
        setHasError(false)
        setErrorMessage("")
        axios
            .post(REGISTRATION_BASE_URL(), { ...payload }, AXIOS_HEADERS)
            .then((response) => {
                setLoading(false)
                const data = response.data
                console.log(data)
                if (data?.status === "USER_ALREADY_EXIST" || data?.status === "USER_RPPS_ALREADY_EXIST") {
                    setHasError(true)
                    setErrorMessage("L'adresse email est déjà utilisé, veuillez choisir un autre.")

                    if (data?.data?.data?.activated === 0) {
                        ShowToast({
                            type: "error",
                            message: "Compte nécessite une activation"
                        })
                        gotoActivation()
                    } else {
                        ShowToast({
                            type: "error",
                            message: "E-mail déjà utilisé, essayer avec un autre."
                        })
                    }
                } else if (data?.status === "ACTIVATION_MAIL_SENT") {
                    setErrorMessage("")
                    gotoActivation()
                    setUserInfo(defaultUserInfos)
                }
            })
            .catch((e) => {
                setLoading(false)
                setHasError(true)
                setErrorMessage("Une erreur est survenue lors de l'inscription")
            })
    }

    const correctPassword =
        isStrongPassword(userInfo.password.first) &&
        isStrongPassword(userInfo.password.second) &&
        areEqual(userInfo.password.first, userInfo.password.second)

    const canSubmit = () =>
        !(
            formValidation.email &&
            userInfo.email.length > 0 &&
            isValideName(userInfo.firstName) &&
            isValideName(userInfo.lastName) &&
            correctPassword
        )

    return (
        <ContainerGlogal>
            <BreeSerifText
                fontSize={30}
                style={{ marginBottom: 20 }}
                textAlign={"center"}
                color={baseColors.green.primary}
            >
                Inscription
            </BreeSerifText>
            <ScrollView keyboardShouldPersistTaps={"handled"} contentContainerStyle={{ paddingBottom: 50 }}>
                <View style={styles.container}>
                    <OpenSansText fontSize={16}>
                        Pour un suivi de meilleure qualité merci de saisir vos vrais noms et prénoms, cela facilitera la
                        communication entre vous et l'équipe soignante.
                    </OpenSansText>
                    <View style={{ marginTop: 20 }}>
                        <InputGroup
                            disabled={loading}
                            marginBottom={5}
                            valid={userInfo.email.length > 0}
                            onChange={(text) => {
                                setUserInfo((old) => ({ ...old, email: text }))
                                setFormValidation((old) => ({
                                    ...old,
                                    email: text.length > 0 && !isValidEmail(text) ? false : true
                                }))
                            }}
                            validation_message={"obligatoire"}
                            label={"Adresse mail"}
                            value={userInfo.email}
                            placeholder={"Entrez votre adresse mail"}
                        />
                        {!formValidation.email && (
                            <OpenSansText fontSize={16} color={baseColors.orange.primary}>
                                Email invalide
                            </OpenSansText>
                        )}
                        <Spacer height={10} />
                        <InputGroup
                            disabled={loading}
                            valid={isStrongPassword(userInfo.password.first)}
                            type={"password"}
                            onChange={(text) =>
                                setUserInfo((old) => ({ ...old, password: { ...old.password, first: text } }))
                            }
                            value={userInfo.password.first}
                            validation_message={"min. 8 caractères"}
                            label={"Mot de passe"}
                            placeholder={"Minimum 8 caractères"}
                        />

                        <InputGroup
                            disabled={loading}
                            valid={isStrongPassword(userInfo.password.second)}
                            type={"password"}
                            onChange={(text) => {
                                setUserInfo((old) => ({ ...old, password: { ...old.password, second: text } }))
                                setFormValidation((old) => ({
                                    ...old,
                                    password: {
                                        ...formValidation.password,
                                        second: areEqual(userInfo.password.first, text)
                                    }
                                }))
                            }}
                            value={userInfo.password.second}
                            validation_message={"min. 8 caractères"}
                            label={"Confirmer le mot de passe"}
                            placeholder={"Minimum 8 caractères"}
                        />
                        {!areEqual(userInfo.password.first, userInfo.password.second) && (
                            <OpenSansText fontSize={16} color={baseColors.orange.primary}>
                                Les mot de passe ne sont pas identique.
                            </OpenSansText>
                        )}
                        <InputGroup
                            disabled={loading}
                            onChange={(text) => setUserInfo((old) => ({ ...old, firstName: text }))}
                            valid={isValideName(userInfo.firstName)}
                            value={userInfo.firstName}
                            validation_message={"obligatoire"}
                            label={"Nom"}
                            placeholder={"Entrer votre nom"}
                        />
                        <InputGroup
                            disabled={loading}
                            onChange={(text) => setUserInfo((old) => ({ ...old, lastName: text }))}
                            valid={isValideName(userInfo.lastName)}
                            value={userInfo.lastName}
                            validation_message={"obligatoire"}
                            label={"Prénom"}
                            placeholder={"Entrez votre prénom"}
                        />
                    </View>
                    {hasError && (
                        <View
                            style={{
                                flexDirection: "row",
                                display: "flex",
                                justifyContent: "space-around",
                                alignItems: "center"
                            }}
                        >
                            {/* <Ionicons name="warning" size={23} color={baseColors.orange.primary} /> */}
                            <BreeSerifText
                                textAlign={"center"}
                                color={baseColors.orange.primary}
                                fontSize={16}
                                fontWeight={"normal"}
                            >
                                {errorMessage}
                            </BreeSerifText>
                        </View>
                    )}

                    {!correctPassword && <PasswordValidatorContent />}

                    <Button
                        loading={loading}
                        disabled={canSubmit() || loading}
                        onPress={performRegister}
                        text="Créer un compte"
                        width="80%"
                        height={45}
                        backgroundColor={baseColors.green.primary}
                        labelColor={"white"}
                    />
                    <OpenSansText
                        style={{ marginTop: 20 }}
                        textAlign={"center"}
                        color={baseColors.green.primary}
                        fontSize={16}
                        fontWeight={"bold"}
                    >
                        Déjà un compte ?{" "}
                    </OpenSansText>
                    <TouchableOpacity activeOpacity={0.5} onPress={() => gotoLoginPage()} disabled={loading}>
                        <OpenSansText
                            style={{ textDecorationLine: "underline" }}
                            textAlign={"center"}
                            color={baseColors.green.primary}
                            fontSize={16}
                            fontWeight={"bold"}
                        >
                            Cliquer pour vous connecter
                        </OpenSansText>
                    </TouchableOpacity>
                </View>
            </ScrollView>
        </ContainerGlogal>
    )
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        paddingTop: 10,
        paddingHorizontal: 20,
        backgroundColor: "white",
        alignContent: "center"
    }
})
